/**
 * Anchored popover — the shell for the column header menu, the row action
 * menu and the column picker.
 *
 * Portalled to `<body>` and positioned from its trigger's rect, flipped above
 * the trigger and clamped into the viewport so it is never clipped by the
 * table or pushed off-screen at 720p. Closes on outside pointer-down, Escape
 * (focus returns to the trigger) and page scroll outside itself.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

const MARGIN = 8;

export interface PopoverProps {
  anchorRef: RefObject<HTMLElement>;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  label: string;
  role?: 'dialog' | 'menu';
  className?: string;
  /** Preferred width in px (clamped to the viewport). */
  width?: number;
  align?: 'start' | 'end';
  testId?: string;
}

function place(
  anchor: HTMLElement | null,
  panel: HTMLElement | null,
  width: number,
  align: 'start' | 'end'
): CSSProperties {
  if (anchor === null || typeof window === 'undefined') return {};
  const rect = anchor.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(width, vw - 2 * MARGIN);
  let left = align === 'end' ? rect.right - w : rect.left;
  left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
  const below = vh - rect.bottom - MARGIN;
  const above = rect.top - MARGIN;
  const height = panel?.scrollHeight ?? 0;
  const isAbove = height > below && above > below;
  const maxHeight = Math.max(160, (isAbove ? above : below) - 4);
  return isAbove
    ? { left, width: w, bottom: vh - rect.top + 4, maxHeight }
    : { left, width: w, top: rect.bottom + 4, maxHeight };
}

export function Popover({
  anchorRef,
  isOpen,
  onClose,
  children,
  label,
  role = 'dialog',
  className,
  width = 280,
  align = 'start',
  testId,
}: PopoverProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' });

  const reposition = useCallback(() => {
    setStyle(place(anchorRef.current, panelRef.current, width, align));
  }, [anchorRef, width, align]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    reposition();
    // Second pass once the content has laid out (height decides flip).
    const frame = globalThis.requestAnimationFrame?.(reposition);
    return () => {
      if (frame !== undefined) globalThis.cancelAnimationFrame?.(frame);
    };
  }, [isOpen, reposition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
      anchorRef.current?.focus();
    };
    const onScroll = (event: Event): void => {
      if (panelRef.current?.contains(event.target as Node)) return;
      reposition();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen, onClose, anchorRef, reposition]);

  useEffect(() => {
    if (!isOpen) return;
    const first = panelRef.current?.querySelector<HTMLElement>(
      '[data-autofocus], button:not([disabled]), input, select, [tabindex="0"]'
    );
    first?.focus({ preventScroll: true });
  }, [isOpen]);

  if (!isOpen) return null;
  return createPortal(
    <div
      ref={panelRef}
      role={role}
      aria-label={label}
      className={`popover${className === undefined ? '' : ` ${className}`}`}
      style={style}
      data-testid={testId}
    >
      {children}
    </div>,
    document.body
  );
}
